import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { ShieldAlert, ArrowRight } from "lucide-react";

/**
 * Modal that gates the publication of a user-generated report/listing.
 * Wires the iter68 → iter79 backend privacy-prompt fields.
 *
 * Flow (iter79):
 *   1) Question step  — user picks NO or YES (no default).
 *   2a) NO            → onDecided({ privacy_prompt_answer: false })
 *   2b) YES → warn    → the ONLY action is "Send to review". The historical
 *       "Publish anyway" checkbox has been removed — no bypass is offered
 *       any more. Backend also refuses the bypass server-side.
 *   3) Non-dismissible via ESC/outside-click so the user can't skip it.
 *
 * The version constant matches the backend's PRIVACY_PROMPT_VERSION.
 */
export const PRIVACY_PROMPT_VERSION = "2026-02";

export default function PrivacyPromptDialog({ open, onCancel, onDecided }) {
  const { t } = useI18n();
  const [step, setStep] = useState("question"); // question | warn
  const [_answer, setAnswer] = useState(null);

  const reset = () => { setStep("question"); setAnswer(null); };

  const chooseNo = () => {
    onDecided({
      privacy_prompt_answer: false,
      privacy_prompt_version: PRIVACY_PROMPT_VERSION,
    });
    reset();
  };

  const chooseYes = () => {
    setAnswer(true);
    setStep("warn");
  };

  const sendToReview = () => {
    onDecided({
      privacy_prompt_answer: true,
      privacy_prompt_version: PRIVACY_PROMPT_VERSION,
    });
    reset();
  };

  const cancel = () => { reset(); onCancel && onCancel(); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) cancel(); }}>
      <DialogContent
        data-testid="privacy-prompt-dialog"
        className="max-w-lg"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {step === "question" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldAlert size={18} className="text-emerald-700" />
                {t("pp_title")}
              </DialogTitle>
              <DialogDescription className="pt-1 text-slate-700">
                {t("pp_question")}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2 grid gap-2">
              <Button
                data-testid="pp-answer-no"
                onClick={chooseNo}
                variant="outline"
                className="justify-start rounded-xl h-12 hover:bg-emerald-50 hover:text-emerald-900"
              >
                <span className="font-semibold">{t("pp_answer_no")}</span>
                <ArrowRight size={14} className="ml-auto opacity-60" />
              </Button>
              <Button
                data-testid="pp-answer-yes"
                onClick={chooseYes}
                variant="outline"
                className="justify-start rounded-xl h-12 hover:bg-amber-50 hover:text-amber-900"
              >
                <span className="font-semibold">{t("pp_answer_yes")}</span>
                <ArrowRight size={14} className="ml-auto opacity-60" />
              </Button>
            </div>
            <DialogFooter className="pt-2">
              <Button
                data-testid="pp-cancel"
                variant="ghost"
                onClick={cancel}
                className="rounded-full hover:bg-slate-100"
              >
                {t("cancel")}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "warn" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-900">
                <ShieldAlert size={18} /> {t("pp_warn_title")}
              </DialogTitle>
              <DialogDescription className="pt-1 text-slate-700 whitespace-pre-line">
                {t("pp_warn_body_review_only")}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-3 grid gap-3">
              <Button
                data-testid="pp-send-review"
                onClick={sendToReview}
                className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white hover:text-white h-11"
              >
                {t("pp_send_review")}
              </Button>
            </div>
            <DialogFooter className="pt-2">
              <Button
                data-testid="pp-back"
                variant="ghost"
                onClick={() => setStep("question")}
                className="rounded-full hover:bg-slate-100"
              >
                {t("back")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
